/**
 * Payment utility functions for checking user payment status
 */

/**
 * Check if user has active payment status
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user has active payment, false otherwise
 */
export const hasActivePayment = (user) => {
  if (!user) return false;
  
  // Check payment status - 'active' means user has paid
  // 'pending', 'cancelled', 'past_due' are inactive states
  return user.paymentStatus === 'active';
};

/**
 * Get payment status message based on user's payment status
 * @param {Object} user - User object from auth context
 * @returns {Object} - Object with message and redirect info
 */
export const getPaymentStatusMessage = (user) => {
  if (!user) {
    return {
      message: 'Authentication required to access this feature.',
      redirectPath: '/login'
    };
  }



  // Check if user has free trial remaining
  if (hasFreeTrial(user)) {
    const remaining = getRemainingFreeTrialDocuments(user);
    return {
      message: `You have ${remaining} free document${remaining === 1 ? '' : 's'} remaining! Upgrade to SignPact Pro for unlimited e-signatures, AI contract templates, and smart contract analysis at just $10/month/user.`,
      redirectPath: '/settings?tab=billing',
      isFreeTrialMessage: true
    };
  }

  // For users without active payment, always redirect to settings billing page
  // This ensures free trial users (even without the new fields) get the right experience
  switch (user.paymentStatus) {
    case 'pending':
      return {
        message: 'Your Free Trial Expired! For just $10/month/user, SignPact offers unlimited e-signatures, AI contract templates, smart contract analysis, and automated form filling. Click here to continue signing contracts with SignPact!',
        redirectPath: '/settings?tab=billing'
      };
    case 'cancelled':
      return {
        message: 'Reactivate Your SignPact Subscription! Get back unlimited e-signatures, AI contract templates, smart contract analysis, and automated form filling for just $10/month/user.',
        redirectPath: '/settings?tab=billing'
      };
    case 'past_due':
      return {
        message: 'Your subscription payment is past due. Update your payment method to restore unlimited e-signatures, AI contract templates, and smart contract analysis.',
        redirectPath: '/settings?tab=billing'
      };
    case 'active':
      return {
        message: 'Payment is active.',
        redirectPath: null
      };
    default:
      return {
        message: 'Unlock Unlimited E-Signatures! Get AI contract templates, smart contract analysis, and automated form filling for just $10/month/user with SignPact Pro.',
        redirectPath: '/settings?tab=billing'
      };
  }
};

/**
 * Check if user is on free trial and still has documents remaining
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user has free trial documents remaining
 */
export const hasFreeTrial = (user) => {
  if (!user) return false;
  
  // Check if user has active payment - if so, no free trial needed
  if (hasActivePayment(user)) return false;
  
  // Check if user still has free trial documents available
  const used = user.freeTrialDocumentsUsed || 0;
  const limit = user.freeTrialDocumentsLimit || 2;
  
  return used < limit;
};

/**
 * Get remaining free trial documents
 * @param {Object} user - User object from auth context
 * @returns {number} - Number of remaining free trial documents
 */
export const getRemainingFreeTrialDocuments = (user) => {
  if (!user) return 0;
  
  const used = user.freeTrialDocumentsUsed || 0;
  const limit = user.freeTrialDocumentsLimit || 2;
  
  return Math.max(0, limit - used);
};

/**
 * Check if user needs payment to access premium features
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if payment is required, false otherwise
 */
export const requiresPayment = (user) => {
  // User has active payment - no payment required
  if (hasActivePayment(user)) return false;
  
  // User still has free trial documents - no payment required yet
  if (hasFreeTrial(user)) return false;
  
  // User has no active payment and no free trial documents left
  return true;
};